import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import {
  ClientSettingName,
  IClientSettings,
  IFlightStatusCardClientConfig,
  IFlightStatusForFlight,
} from "@vmosolutions/oc-interfaces";
import AmplifyAPIWrapper from "src/API/AmplifyAPIWrapper";
import { getClientSetting } from "../actions/App.Actions";

export enum ComponentNames {
  FlightInfo = "FlightInfo",
  Weather = "Weather",
  CrewInfo = "CrewInfo",
  NextEvent = "NextEvent",
  ContentQuickAccess = "ContentQuickAccess",
  FlightRelease = "FlightRelease",
}

export interface IStatusMsg {
  severity: "error" | "warning" | "success" | "info";
  msg: string;
}

interface ColumnDef {
  [key: string]: IFlightStatusCardClientConfig;
}

export interface IFlightStatusSort {
  dataPath: string;
  type: "number" | "string" | "error" | "date" | "count";
  label: string;
  default: boolean;
}

export interface IFlightStatusState {
  columnDefs: ColumnDef[];
  settings: {
    disabled: boolean;
    windowBack: number;
    windowForward: number;
  };
  autoScrollSettings: {
    incrementAmount: number;
    interval: number;
  };
  sorting: IFlightStatusSort[];
  flightData?: any;
  msg: IStatusMsg | null;
  updating: boolean;
  loading: boolean;
}

const initialState: IFlightStatusState = {
  columnDefs: [],
  settings: {
    disabled: false,
    windowBack: 0,
    windowForward: 0,
  },
  autoScrollSettings: {
    incrementAmount: 5,
    interval: 1000,
  },
  sorting: [],
  flightData: [],
  msg: null,
  updating: false,
  loading: true,
};

export const getColumnDefs = createAsyncThunk("flightStatus/getColumnDefs", async (thunkApi) => {
  const columnDefinitionSettings: IClientSettings = await getClientSetting(ClientSettingName.FlightStatus);
  return columnDefinitionSettings;
});

export const getFlightData = createAsyncThunk("flightStatus/getFlightData", async (thunkApi) => {
  const res = await AmplifyAPIWrapper.Get("/flightstatus");
  return res;
});

export const getFlightDataByID = createAsyncThunk(
  "flightStatus/getFlightDataByID",
  async (flightID: string, thunkApi) => {
    const res = await AmplifyAPIWrapper.Get(`/flightstatus/${flightID}`);
    return res;
  }
);

export const flightStatus: any = createSlice({
  name: "flightStatus",
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setMsg: (state, action: PayloadAction<IStatusMsg | null>) => {
      state.msg = action.payload;
    },
    setUpdating: (state, action: PayloadAction<boolean>) => {
      state.updating = action.payload;
    },
    webSocketUpdateFlightStatus: (state, action: PayloadAction<IFlightStatusForFlight>) => {
      const { flightId, cards } = action.payload;
      if (flightId) {
        const newFlightDetails = [...state.flightData];
        const newFlightDetailIdx = newFlightDetails.findIndex((flight: any) => flight.flightId === flightId);
        if (newFlightDetailIdx !== -1) {
          const newCards = { ...newFlightDetails[newFlightDetailIdx].cards };

          newFlightDetails[newFlightDetailIdx] = {
            ...newFlightDetails[newFlightDetailIdx],
            cards: { ...newCards, ...cards },
          };
          state.flightData = newFlightDetails;
          state.updating = false;
        }
      }
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getFlightData.fulfilled, (state, action: PayloadAction<any>) => {
        state.flightData = action.payload;
      })
      .addCase(getColumnDefs.fulfilled, (state, action: PayloadAction<any>) => {
        const cards = action.payload.setValue.cards;
        const sorting = action.payload.setValue.sorting;
        const autoScrollSettings = action.payload.setValue.autoScrollSettings;

        const enabledCards: any[] = [];
        Object.keys(cards).forEach((card) => {
          if (!cards[card].disabled) {
            enabledCards.push(cards[card].display);
          }
        });
        enabledCards.sort((a: any, b: any) => {
          if (a.colOrder - b.colOrder) {
            return 1;
          } else {
            return -1;
          }
        });
        state.columnDefs = enabledCards;
        state.sorting = sorting;
        state.autoScrollSettings = autoScrollSettings;
        state.settings = {
          disabled: action.payload.setValue.disabled,
          windowBack: action.payload.setValue.windowBack,
          windowForward: action.payload.setValue.windowForward,
        };
      });
  },
});

export const { setMsg, webSocketUpdateFlightStatus, setUpdating, setLoading } = flightStatus.actions;

export default flightStatus.reducer;
